import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faClipboard } from '@fortawesome/pro-solid-svg-icons/faClipboard';
import clsx from 'clsx';
import { type FC, type PropsWithChildren, useEffect, useState } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import { FontAwesomeSvgIcon } from 'react-fontawesome-svg-icon';

import { IgnoredError, ReportError } from '@furality/frontend-stack';
import { Button } from '@furality/furality-purpose';
import copyToClipboard from '#/hooks/copyToClipboard';
import useTimedState from '#/hooks/useTimedState';

import chevron from './ChevronToggle.module.scss';
import Foldable from './Foldable';

const parseBody = (body: string | undefined) => {
  if (body === undefined) {
    return undefined;
  }
  try {
    return JSON.parse(body) as unknown;
  } catch {
    return body;
  }
};

// A fetch Response serialises to `{}`, so pick out the useful fields by hand.
const describeResponse = (response: Response, body: string | undefined) => ({
  status: response.status,
  statusText: response.statusText,
  url: response.url,
  body: parseBody(body),
});

// The generated clients only read the body for successful responses, so read it here.
const useResponseBody = (error: unknown) => {
  const [body, setBody] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    if (error instanceof ReportError || error instanceof IgnoredError) {
      const response = error.response.response;
      if (!response.bodyUsed) {
        response
          .clone()
          .text()
          .then(
            (text) => {
              if (!cancelled) {
                setBody(text);
              }
            },
            () => {
              /* body unreadable, show the rest */
            },
          );
      }
    }
    return () => {
      cancelled = true;
    };
  }, [error]);

  return body;
};

const formatError = (error: unknown, responseBody: string | undefined) => {
  if (error instanceof ReportError || error instanceof IgnoredError) {
    const response = error.response;
    // @ts-expect-error validator not worth logging
    delete response.validator;
    try {
      // @ts-expect-error TODO: request should always exist?
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      response.request.parameters.config.auth.bearer = '<censored>';
    } catch {
      /* empty */
    }
    return {
      title: error.name,
      body: `${error.message}${error.cause ? `${JSON.stringify(error.cause)}\n` : ''}\n${JSON.stringify({ ...response, response: describeResponse(response.response, responseBody) }, null, 2)}`,
      fold: true,
    };
  }

  if (error instanceof Error) {
    return {
      title: error.name,
      body: `${error.message}${error.cause ? `${JSON.stringify(error.cause)}\n` : ''}`,
      fold: true,
    };
  }

  return {
    title: 'Unknown Error',
    body: JSON.stringify(error, null, 2),
    fold: true,
  };
};

export const GooseDefault = () => (
  <>
    Please report this in the{' '}
    <Alert.Link
      href="https://discord.com/channels/693598922064789635/927516390108327976"
      className="text-underline"
    >
      DevOps channel
    </Alert.Link>
    .
  </>
);

type ErrorDisplayProps = {
  error: unknown;
  title?: string;
};
const ErrorDisplay: FC<PropsWithChildren<ErrorDisplayProps>> = ({
  error,
  title = 'An error occurred',
  children,
}) => {
  const responseBody = useResponseBody(error);
  const { title: errorTitle, body, fold } = formatError(error, responseBody);
  const [copied, setCopied] = useTimedState(false);
  const [show, setShow] = useState(false);

  return (
    <Alert variant="danger" className="tw:max-w-[80ch]">
      <Alert.Heading>{title}</Alert.Heading>
      {children ?? <GooseDefault />}
      <Row className="align-items-center mt-2">
        <Col xs="auto">
          <strong>{errorTitle}</strong>
          {body && fold && show && ':'}
        </Col>
        <Col />
        <Col xs="auto">
          {fold && (
            <Button
              size="sm"
              className="p-md-1 px-md-2"
              onClick={() => setShow((f) => !f)}
              icon={
                <FontAwesomeSvgIcon
                  icon={faChevronDown}
                  className={clsx(chevron['chevronToggle'], show && chevron['cwToggled'])}
                />
              }
              iconPosition="end"
            >
              {show ? 'Hide' : 'Show'}
            </Button>
          )}
          <Button
            size="sm"
            type="button"
            className="p-md-1 px-md-2"
            onClick={() =>
              copyToClipboard(
                `\`\`\`json\nTitle: ${title}\nError: ${errorTitle}\n${body && `${body}\n`}\n\`\`\``,
              ).then(() => setCopied(true))
            }
            icon={<FontAwesomeSvgIcon icon={copied ? faCheck : faClipboard} />}
            iconPosition="end"
            variant={copied ? 'success' : 'primary'}
          >
            {copied ? 'Copied' : 'Copy error'}
          </Button>
        </Col>
      </Row>
      <Foldable show={fold && show}>
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            overflowX: 'hidden',
          }}
          className="mw-100 mt-2 mb-0"
        >
          {body}
        </pre>
      </Foldable>
    </Alert>
  );
};

export default ErrorDisplay;
